import type { Client } from "urql"

import { ExternalLocationType, LocationBaseListDocument, type Product } from "@/graphql/codegen/graphql"
import { isProductsInfoValid } from "@/screens/Procurement/PurchaseOrder/utils/common"

interface ValidationResult {
  isValid: boolean
  message?: string
}

interface ValidatePurchaseOrderCreationParams {
  products: Product[] | null
  companyId: string
  client: Client
}

export const validatePurchaseOrderCreation = async ({
  products,
  companyId,
  client,
}: ValidatePurchaseOrderCreationParams): Promise<ValidationResult> => {
  // Check 1: Product packaging information
  if (!isProductsInfoValid(products)) {
    return {
      isValid: false,
      message: "Couldn't create PO. Please make sure all required product packaging information is complete",
    }
  }

  // Check 2: Location associations
  if (!products || products.length === 0) {
    return {
      isValid: false,
      message: "The company requires a Location Association before a Purchase Order can be created",
    }
  }

  // Check 3: Vendor has ORDER locations
  const result = await client
    .query(LocationBaseListDocument, {
      input: {
        filter: {
          companyId,
          externalLocationTypes: [ExternalLocationType.Order],
        },
      },
    })
    .toPromise()

  const hasOrderLocations =
    result.data?.location?.list?.__typename === "LocationListSuccess" &&
    (result.data.location.list.pageInfo?.total ?? 0) > 0

  if (!hasOrderLocations) {
    return {
      isValid: false,
      message:
        "This vendor has no ORDER locations configured. Add an ORDER location in Company Management before creating a purchase order.",
    }
  }

  // All checks passed
  return { isValid: true }
}
